// apps/web/src/components/PwaUpdatePrompt.tsx
//
// Ponto único e global do ciclo de atualização do PWA. Registra o service
// worker (useRegisterSW), verifica versão nova periodicamente / no foco / em
// cada navegação SPA, e mostra dois toasts de 1 clique — nunca recarrega
// sozinho:
//
//   1. Versão nova pronta ("Nova versão — Atualizar"): o clique chama
//      updateServiceWorker(), que envia SKIP_WAITING ao SW em waiting; o
//      vite-plugin-pwa recarrega no evento `controlling`, entregando a versão
//      publicada sem Ctrl+Shift+R.
//   2. Chunk stale (import lazy falhou por bundle antigo removido do edge): o
//      chunkReload emite `alamo:chunk-stale`; o clique aqui ativa o SW novo. Se
//      ainda NÃO há SW em waiting (o chunk falhou antes do update() promover o
//      SW novo), primeiro dispara registration.update() e espera o SW novo
//      chegar a waiting antes de pular — senão um reload cru reentraria no
//      MESMO SW velho, servindo o index.html precacheado antigo e falhando o
//      chunk de novo.
//
// Todo reload é consequência do clique do usuário (o clique é o consentimento;
// a espera pelo SW novo é interna ao clique) — nunca automático, para não
// descartar dados digitados em formulário/cotação abertos.
//
// O registro do SW aqui é o mesmo que serve o Web Push (sw-push.js é importado
// pelo SW gerado via workbox.importScripts em vite.config.ts).

import { useEffect, useRef, useState } from 'react';

import { RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { Button, Spinner } from '@alamo/ui';

import { consumePendingChunkStale } from '../lib/chunkReload';
import { waitForWaitingWorker } from '../lib/swUpdateChoreography';
import { router } from '../routes';

// O browser só reavalia o SW em navegação/~24h por conta própria; forçamos uma
// checagem ativa a cada 60s para detectar deploys rapidamente.
const UPDATE_CHECK_INTERVAL_MS = 60_000;
// Se após o clique nenhum SW novo aparecer neste prazo, o chunk provavelmente
// falhou por rede transitória (não por deploy) — reload cru como último recurso.
const NEW_SW_WAIT_TIMEOUT_MS = 7_000;
const UPDATE_TOAST_ID = 'pwa-update-available';
const CHUNK_STALE_TOAST_ID = 'pwa-chunk-stale';

function ChunkStaleToast({
  onReload,
  label,
  action,
}: {
  onReload: () => Promise<void>;
  label: string;
  action: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex items-center gap-3">
      <RefreshCw className="size-4 shrink-0 text-laranja-encanto" aria-hidden />
      <span className="text-sm font-medium text-ink">{label}</span>
      <Button
        size="sm"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void onReload();
        }}
      >
        {busy ? <Spinner size="sm" /> : action}
      </Button>
    </div>
  );
}

export function PwaUpdatePrompt(): null {
  const { t } = useTranslation(['ui']);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      registrationRef.current = registration;
      intervalRef.current = setInterval(() => {
        void registration.update().catch(() => undefined);
      }, UPDATE_CHECK_INTERVAL_MS);
    },
    onRegisterError(error) {
      // Falha de registro deixa o app sem mecanismo de detecção de update —
      // logar em DEV para não passar silenciosamente.
      if (import.meta.env.DEV) {
        console.error('[pwa] falha ao registrar o service worker', error);
      }
    },
  });

  // Checa update quando a aba volta a ficar visível, no intervalo, e em cada
  // navegação SPA (deploy detectado mais rápido que o intervalo de 60s).
  useEffect(() => {
    function requestUpdateCheck(): void {
      void registrationRef.current?.update().catch(() => undefined);
    }
    function checkForUpdateOnVisible(): void {
      if (document.visibilityState === 'visible') requestUpdateCheck();
    }

    document.addEventListener('visibilitychange', checkForUpdateOnVisible);
    let lastPath = router.state.location.pathname;
    const unsubscribeRouter = router.subscribe((state) => {
      if (state.location.pathname !== lastPath) {
        lastPath = state.location.pathname;
        requestUpdateCheck();
      }
    });

    return () => {
      document.removeEventListener('visibilitychange', checkForUpdateOnVisible);
      unsubscribeRouter();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Versão nova pronta ⇒ toast persistente com ação de atualizar (1 clique).
  useEffect(() => {
    if (!needRefresh) return;
    toast(
      (toastInstance) => (
        <div className="flex items-center gap-3">
          <RefreshCw className="size-4 shrink-0 text-laranja-encanto" aria-hidden />
          <span className="text-sm font-medium text-ink">{t('ui:pwa.update_available')}</span>
          <Button
            size="sm"
            onClick={() => {
              toast.dismiss(toastInstance.id);
              void updateServiceWorker();
            }}
          >
            {t('ui:pwa.update_action')}
          </Button>
        </div>
      ),
      { id: UPDATE_TOAST_ID, duration: Infinity },
    );
  }, [needRefresh, t, updateServiceWorker]);

  // Chunk stale ⇒ toast persistente. O reload só ocorre no clique.
  useEffect(() => {
    // Coreografia do clique (mantém a invariante: reload só depois do clique):
    //   - há SW em waiting  → updateServiceWorker() → SKIP_WAITING → o plugin
    //     recarrega no evento `controlling`.
    //   - sem waiting        → registration.update() e espera o SW novo chegar a
    //     waiting; então updateServiceWorker() (mesmo caminho acima).
    //   - timeout sem SW novo → reload cru como último recurso (falha de rede,
    //     não deploy).
    async function performChunkReload(): Promise<void> {
      const registration = registrationRef.current;
      if (!registration) {
        window.location.reload();
        return;
      }
      if (registration.waiting) {
        await updateServiceWorker();
        return;
      }
      const found = await waitForWaitingWorker(registration, NEW_SW_WAIT_TIMEOUT_MS);
      if (found) {
        await updateServiceWorker();
      } else {
        window.location.reload();
      }
    }

    function showChunkStaleToast(): void {
      toast(
        (toastInstance) => (
          <ChunkStaleToast
            label={t('ui:pwa.chunk_stale')}
            action={t('ui:pwa.chunk_stale_action')}
            onReload={async () => {
              await performChunkReload();
              toast.dismiss(toastInstance.id);
            }}
          />
        ),
        { id: CHUNK_STALE_TOAST_ID, duration: Infinity },
      );
    }

    // Drena um evento perdido durante o boot (chunk falhou antes deste mount).
    if (consumePendingChunkStale()) showChunkStaleToast();

    window.addEventListener('alamo:chunk-stale', showChunkStaleToast);
    return () => window.removeEventListener('alamo:chunk-stale', showChunkStaleToast);
  }, [t, updateServiceWorker]);

  return null;
}
